/*
 * UI watchdog: detects "page frozen" states (an invisible layer blocks clicks or
 * a stale scroll lock) and reports them to GA4 as event `ui_stuck`.
 * Theme-owned stuck states are healed right away; third-party layers are only reported.
 *
 * GA4 parameters (register as custom dimensions to use them in reports):
 *   stuck_type  header_overlay | modal_overlay | modal_wrapper | scroll_lock | rage_click
 *   blocker     element that received the click
 *   body_class  body classes at detection time
 *   open_layers theme modals/menus that were open
 *   recent      last UI actions before the incident, newest first
 *   healed      "yes" if the watchdog resolved the state itself
 */
(function () {
  const script = document.currentScript;
  const GA4_ID = script && script.dataset.ga4Id;
  const MAX = 100; // GA4 limit for parameter values
  const reported = new Set();
  const recent = [];
  const openedAt = new WeakMap();

  window.dataLayer = window.dataLayer || [];
  function gtagPush() { window.dataLayer.push(arguments); }

  const describe = (el) => {
    if (!el || !el.tagName) return '';
    const id = el.id ? `#${el.id}` : '';
    const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).slice(0, 3).join('.') : '';
    return `${el.tagName.toLowerCase()}${id}${cls ? '.' + cls : ''}`;
  };

  const isModal = (el) => typeof ModalElement !== 'undefined' && el instanceof ModalElement;
  const openModals = () => [...document.querySelectorAll('[open]')].filter((el) => isModal(el) && !el.hidden);
  const openMenus = () => [...document.querySelectorAll('details[is="details-dropdown"][open], details[is="details-mega"][open]')];

  const log = (label) => {
    recent.unshift({ label, time: Date.now() });
    recent.length = Math.min(recent.length, 6);
  };

  const report = (type, data = {}) => {
    const key = `${type}|${data.blocker || ''}`;
    if (reported.has(key)) return; // once per page view and blocker
    reported.add(key);

    const now = Date.now();
    const params = {
      stuck_type: type,
      blocker: data.blocker || '',
      body_class: data.bodyClass || document.body.className,
      open_layers: [...openModals(), ...openMenus()].map(describe).join(' ') || 'none',
      recent: recent.map((entry) => `${entry.label}@${((now - entry.time) / 1000).toFixed(1)}s`).join(' ') || 'none',
      healed: data.healed ? 'yes' : 'no',
      page_template: document.body.className.match(/template-[\w-]+/)?.[0] || '',
      pointer: data.pointer || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`
    };
    Object.keys(params).forEach((name) => { params[name] = String(params[name]).slice(0, MAX); });
    if (GA4_ID) params.send_to = GA4_ID;

    gtagPush('event', 'ui_stuck', params);
    if (window.console) console.warn('[ui-watchdog]', params);
  };

  // Recent UI actions, sent along to reconstruct what led to the incident
  document.addEventListener('toggle', (event) => {
    if (!isModal(event.target)) return;
    if (event.target.open) openedAt.set(event.target, Date.now());
    log(`${event.target.tagName.toLowerCase()}${event.target.open ? '+' : '-'}`);
  }, true);
  document.addEventListener('theme.cart.added', () => log('cart_added'));
  document.addEventListener('visibilitychange', () => log(document.visibilityState === 'hidden' ? 'tab_hidden' : 'tab_visible'));
  window.addEventListener('pageshow', (event) => event.persisted && log('bfcache_restore'));
  document.addEventListener('mouseover', (event) => {
    const menu = event.target.closest?.('details[is="details-mega"], details[is="details-dropdown"]');
    if (menu && recent[0]?.label !== 'menu_hover') log('menu_hover');
  }, { passive: true });

  const coversViewport = (el) => {
    const style = window.getComputedStyle(el);
    if (style.position !== 'fixed') return false;
    const rect = el.getBoundingClientRect();
    return rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9;
  };

  // Clicks that land on a theme layer which should not be interactive right now
  const checkThemeLayer = (target, pointer) => {
    const bodyClass = document.body.className; // snapshot before healing
    if (target.matches('.header ~ .overlay') && openMenus().length === 0) {
      document.body.classList.remove('has-dropdown-menu', 'with-mega');
      report('header_overlay', { blocker: describe(target), healed: true, pointer, bodyClass });
      return true;
    }

    const modal = target.closest('.drawer, .x-modal');
    if (!modal || !isModal(modal)) return false;

    if (!modal.open && target.closest('.overlay')) {
      modal.removeAttribute('active');
      modal.hidden = true;
      report('modal_overlay', { blocker: describe(modal), healed: true, pointer, bodyClass });
      return true;
    }

    // Click on the bare full-screen wrapper of an open modal whose panel is not visible
    if (modal.open && target === modal && Date.now() - (openedAt.get(modal) || 0) > 1500) {
      const inner = modal.querySelector('.drawer__inner');
      const rect = inner && inner.getBoundingClientRect();
      const offscreen = !rect || rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth;
      if (offscreen) {
        modal.hide();
        report('modal_wrapper', { blocker: describe(modal), healed: true, pointer, bodyClass });
        return true;
      }
    }
    return false;
  };

  // Repeated clicks directly on a full-screen layer (e.g. third-party popups)
  let rage = { el: null, count: 0, first: 0 };
  const checkRageClick = (target, pointer) => {
    if (!coversViewport(target)) return;

    const now = Date.now();
    if (rage.el !== target || now - rage.first > 2000) rage = { el: target, count: 0, first: now };
    rage.count += 1;

    if (rage.count >= 3) report('rage_click', { blocker: describe(target), pointer });
  };

  document.addEventListener('pointerdown', (event) => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    if (!checkThemeLayer(event.target, event.pointerType)) checkRageClick(event.target, event.pointerType);
  }, true);

  // Scroll lock without any open modal. Re-checked after a delay because the
  // lock is released a moment after the close transition.
  let scrollCheckPending = false;
  const isScrollLockStuck = () => document.body.matches('.has-modal-open, .has-modal-opening') && !openModals().some((modal) => modal.shouldLock);
  const checkScrollLock = () => {
    if (scrollCheckPending || !isScrollLockStuck()) return;
    scrollCheckPending = true;
    setTimeout(() => {
      scrollCheckPending = false;
      if (!isScrollLockStuck()) return;
      const bodyClass = document.body.className;
      document.body.classList.remove('has-modal-open', 'has-modal-opening');
      report('scroll_lock', { blocker: 'body', healed: true, bodyClass });
    }, 2000);
  };
  window.addEventListener('wheel', checkScrollLock, { passive: true });
  window.addEventListener('touchmove', checkScrollLock, { passive: true });
})();
